import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Gift, PenLine, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import { useCart } from "@/lib/cart";
import { apiPost } from "@/lib/api";
import LiveSeats from "@/components/LiveSeats";
import { BOOK, EVENT } from "@/lib/data";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.2em] text-[#8A99B5]">{label}</span>
    <input
      {...props}
      className="mt-2 w-full bg-[#131B36] border border-white/10 rounded-xl px-4 py-3 text-sm text-[#F8F9FA] placeholder:text-[#8A99B5]/60 focus:outline-none focus:border-[#D4AF37]/60 transition-colors"
    />
  </label>
);

const Checkout = () => {
  const { seats, total, clear } = useCart();
  const navigate = useNavigate();

  const [buyer, setBuyer] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [isGift, setIsGift] = useState(false);
  const [recipient, setRecipient] = useState({ name: "", email: "", message: "" });
  const [bookAddon, setBookAddon] = useState(false);
  const [busy, setBusy] = useState(false);

  if (seats.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <LogoMark size={56} />
        <h1 className="font-display text-3xl mt-8 text-[#F8F9FA]">No seats selected yet</h1>
        <p className="text-[#8A99B5] mt-3">Choose where you'd like to sit in the light.</p>
        <Link to="/" data-testid="back-to-seats-button" className="btn-gold rounded-full px-8 py-3.5 text-sm font-bold mt-8">
          Back to the Seat Map
        </Link>
      </div>
    );
  }

  const bookTotal = bookAddon ? BOOK.signedAddonPrice : 0;
  const grand = total + bookTotal;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const order = await apiPost("/api/orders", {
        buyer,
        seat_ids: seats.map((s) => s.id),
        is_gift: isGift,
        recipient,
        book_addon: bookAddon,
      });
      clear();
      navigate(`/confirmation/${order.code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <header className="glass sticky top-0 z-50 py-4">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <LogoMark size={34} />
            <span className="font-cinzel text-gold-gradient tracking-[0.14em] text-sm">A VIEW FROM THE TOP</span>
          </Link>
          <Link to="/" className="text-sm text-[#C5CEE0] hover:text-[#F3D068] transition-colors inline-flex items-center gap-2">
            <ArrowLeft size={15} /> Back to the event
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-14">
        <h1 className="font-display text-4xl sm:text-5xl text-[#F8F9FA]">
          Hold your <em className="text-gold-gradient italic">seats</em>
        </h1>
        <p className="text-[#8A99B5] font-light mt-3">
          {EVENT.dateLabel} · {EVENT.venue}, Beverly Hills · Doors {EVENT.doors}
        </p>

        <form onSubmit={submit} className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 mt-12 items-start">
          <div className="space-y-8">
            {/* Buyer */}
            <section className="glass rounded-3xl p-7">
              <h2 className="font-display text-2xl text-[#F8F9FA]">Your details</h2>
              <div className="grid sm:grid-cols-2 gap-5 mt-6">
                <Field
                  label="First name"
                  required
                  data-testid="buyer-first-name-input"
                  value={buyer.first_name}
                  onChange={(e) => setBuyer({ ...buyer, first_name: e.target.value })}
                  placeholder="Jane"
                />
                <Field
                  label="Last name"
                  required
                  data-testid="buyer-last-name-input"
                  value={buyer.last_name}
                  onChange={(e) => setBuyer({ ...buyer, last_name: e.target.value })}
                  placeholder="Moreno"
                />
                <Field
                  label="Email"
                  type="email"
                  required
                  data-testid="buyer-email-input"
                  value={buyer.email}
                  onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                  placeholder="jane@email.com"
                />
                <Field
                  label="Phone (optional)"
                  data-testid="buyer-phone-input"
                  value={buyer.phone}
                  onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })}
                  placeholder="(310) 555-0134"
                />
              </div>
            </section>

            {/* Gift */}
            <section className="glass rounded-3xl p-7">
              <label className="flex items-start gap-4 cursor-pointer select-none">
                <button
                  type="button"
                  role="switch"
                  aria-checked={isGift}
                  data-testid="checkout-gift-checkbox"
                  onClick={() => setIsGift(!isGift)}
                  className={`mt-1 w-11 h-6 rounded-full transition-colors relative shrink-0 ${
                    isGift ? "bg-[#D4AF37]" : "bg-white/15"
                  }`}
                >
                  <span
                    className="absolute w-5 h-5 rounded-full bg-white shadow-[0_1px_4px_rgba(0,0,0,0.4)]"
                    style={{
                      left: 0,
                      top: 2,
                      transform: isGift ? "translateX(22px)" : "translateX(2px)",
                      transition: "transform 0.25s cubic-bezier(0.22,1,0.36,1)",
                    }}
                  />
                </button>
                <span>
                  <span className="flex items-center gap-2 text-[#F8F9FA] font-medium">
                    <Gift size={16} className="text-[#F3D068]" /> This is a gift for someone who is grieving
                  </span>
                  <span className="block text-sm text-[#8A99B5] font-light mt-1.5">
                    We'll send them a beautiful digital gift pass with your message — their seats
                    will be waiting under their name.
                  </span>
                </span>
              </label>

              {isGift && (
                <div className="grid sm:grid-cols-2 gap-5 mt-7">
                  <Field
                    label="Recipient's name"
                    required={isGift}
                    data-testid="checkout-recipient-name-input"
                    value={recipient.name}
                    onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                    placeholder="Aunt Carol"
                  />
                  <Field
                    label="Recipient's email"
                    type="email"
                    required={isGift}
                    data-testid="checkout-recipient-email-input"
                    value={recipient.email}
                    onChange={(e) => setRecipient({ ...recipient, email: e.target.value })}
                    placeholder="carol@email.com"
                  />
                  <label className="block sm:col-span-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-[#8A99B5]">
                      Your note to them ({recipient.message.length}/200)
                    </span>
                    <textarea
                      rows={3}
                      maxLength={200}
                      data-testid="checkout-gift-message-textarea"
                      value={recipient.message}
                      onChange={(e) => setRecipient({ ...recipient, message: e.target.value })}
                      placeholder="This seat is saved for you. You don't have to be strong that night — just come."
                      className="mt-2 w-full bg-[#131B36] border border-white/10 rounded-2xl px-4 py-3 text-sm text-[#F8F9FA] placeholder:text-[#8A99B5]/60 focus:outline-none focus:border-[#D4AF37]/60 transition-colors resize-none"
                    />
                  </label>
                </div>
              )}
            </section>

            {/* Book add-on */}
            <section className="glass rounded-3xl p-7">
              <label className="flex items-start gap-4 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={bookAddon}
                  onChange={(e) => setBookAddon(e.target.checked)}
                  data-testid="checkout-book-addon-checkbox"
                  className="mt-1 w-5 h-5 accent-[#D4AF37]"
                />
                <span>
                  <span className="flex items-center gap-2 text-[#F8F9FA] font-medium">
                    <PenLine size={16} className="text-[#F3D068]" /> Add a signed copy of Infinite Life, Infinite Lessons — ${BOOK.signedAddonPrice}
                  </span>
                  <span className="block text-sm text-[#8A99B5] font-light mt-1.5">
                    Pick it up signed at intermission. Or{" "}
                    <a href={BOOK.amazonUrl} target="_blank" rel="noopener noreferrer" className="text-[#F3D068] underline underline-offset-2">
                      order from Amazon
                    </a>{" "}
                    to have it shipped.
                  </span>
                </span>
              </label>
            </section>
          </div>

          {/* Summary */}
          <aside className="glass rounded-3xl p-7 lg:sticky lg:top-28">
            <h2 className="font-display text-2xl text-[#F8F9FA]">Your seats</h2>
            <div className="mt-5 space-y-3 max-h-64 overflow-y-auto pr-1">
              {seats.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm" data-testid={`summary-seat-${s.id}`}>
                  <span className="text-[#C5CEE0]">
                    <span className={s.zone === "vip" ? "text-[#F3D068]" : ""}>
                      {s.zone === "vip" ? "VIP" : s.zoneLabel}
                    </span>{" "}
                    · Row {s.row}, Seat {s.n}
                  </span>
                  <span className="text-[#F8F9FA] font-medium">${s.price}</span>
                </div>
              ))}
            </div>
            <div className="hairline my-5" />
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between text-[#C5CEE0]">
                <span>Subtotal</span>
                <span>${total}</span>
              </div>
              {bookAddon && (
                <div className="flex justify-between text-[#C5CEE0]">
                  <span>Signed book</span>
                  <span>${bookTotal}</span>
                </div>
              )}
              <div className="flex justify-between text-[#F8F9FA] font-display text-2xl pt-2">
                <span>Total</span>
                <span data-testid="checkout-total-amount" className="text-gold-gradient">${grand}</span>
              </div>
            </div>

            <div className="mt-5 rounded-2xl bg-[#F59E0B]/5 border border-[#F59E0B]/20 px-4 py-3" data-testid="checkout-seats-left">
              <LiveSeats />
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="checkout-submit-button"
              className="btn-gold w-full rounded-full py-4 text-sm font-bold tracking-wide mt-7 disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Holding your seats…
                </>
              ) : isGift ? (
                "Send This Gift"
              ) : (
                "Complete My Reservation"
              )}
            </button>
            <p className="flex items-center justify-center gap-2 text-xs text-[#8A99B5] mt-4 text-center">
              <ShieldCheck size={14} className="text-[#F3D068]" />
              Seats are held instantly — payment link arrives by email.
            </p>
          </aside>
        </form>
      </main>
    </div>
  );
};

export default Checkout;
